import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { setAuthContext } from '../services/apiClient';
import authService from '../services/authService';

const AuthContext = createContext();

/**
 * AuthProvider — 인증 상태를 전역으로 관리하는 Provider
 *
 * 제공하는 값:
 * - user (object | null): 현재 로그인한 사용자 정보
 * - isLoading (boolean): 초기 토큰 검증 중 여부
 * - isLoggedIn (boolean): 인증 여부 (!!user)
 * - handleLogin(userData, token): 로그인 처리
 * - handleLogout(): 로그아웃 처리
 * - updateUser(userData): 사용자 정보 업데이트
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  /**
   * 로그인 처리
   * 토큰을 localStorage에 저장 → user 상태 업데이트 → /dashboard로 이동
   * @param {object} userData - 사용자 정보 객체
   * @param {string} token - JWT 토큰
   */
  const handleLogin = useCallback(
    (userData, token) => {
      localStorage.setItem('token', token);
      setUser(userData);
      navigate('/dashboard');
    },
    [navigate]
  );

  /**
   * 로그아웃 처리
   * authService.logout() 호출 → 토큰 제거 → user null → /login으로 이동
   */
  const handleLogout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('로그아웃 오류:', error);
    } finally {
      localStorage.removeItem('token');
      setUser(null);
      navigate('/login');
    }
  }, [navigate]);

  /**
   * 사용자 정보 업데이트
   * @param {object} userData - 업데이트할 사용자 정보
   */
  const updateUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  // 마운트 시 localStorage 토큰 확인 → authService.getMe() → user 설정 또는 토큰 제거
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await authService.getMe();
        setUser(data.user);
      } catch (error) {
        console.error('토큰 검증 실패:', error);
        localStorage.removeItem('token');
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, []);

  // Context value를 useMemo로 메모이제이션하여 불필요한 리렌더 방지
  const value = useMemo(
    () => ({
      user,
      isLoading,
      isLoggedIn: !!user,
      handleLogin,
      handleLogout,
      updateUser,
    }),
    [user, isLoading, handleLogin, handleLogout, updateUser]
  );

  // apiClient에 AuthContext 참조 주입 (401 자동 로그아웃 연동)
  useEffect(() => {
    setAuthContext(value);
  }, [value]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth 커스텀 훅
 * AuthProvider 외부에서 호출 시 에러를 throw한다.
 * @returns {{ user, isLoading, isLoggedIn, handleLogin, handleLogout, updateUser }}
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
